<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="products-menu">
    <div class="products-menu__body">
      <div class="products-menu__nav" role="tablist">
        <template v-for="(productsTab, tabIndex) in mainCategories" :key="productsTab.id">
          <div
            v-if="productsTab.items?.length"
            :ref="(el) => setNavItemRef(el, productsTab.id)"
            class="products-menu__nav-item"
            :tabindex="activeTabId === productsTab.id ? 0 : -1"
            :class="{ 'is-active': activeTabId === productsTab.id }"
            role="tab"
            aria-disabled="false"
            :aria-selected="activeTabId === productsTab.id"
            @click="onTabChange(productsTab.id)"
            @keydown.enter.prevent="onTabChange(productsTab.id)"
            @keydown.space.prevent="onTabChange(productsTab.id)"
            @keydown="onNavItemKeydown($event, productsTab, tabIndex)">
            <span class="products-menu__nav-item-label">{{ productsTab.name }}</span>
            <span class="products-menu__nav-item-arrow">
              <zm-icon size="12"><ChevronRightV2 /></zm-icon>
            </span>
          </div>
          <a
            v-else
            :ref="(el) => setNavItemRef(el, productsTab.id)"
            :href="productsTab.link"
            :tabindex="activeTabId === productsTab.id ? 0 : -1"
            target="_blank"
            class="products-menu__nav-item"
            @keydown="onNavItemKeydown($event, productsTab, tabIndex)"
            @click.prevent="
              onActionLinkClick(productsTab.name, toAbsoluteLink(productsTab.link), '_blank')
            ">
            <span class="products-menu__nav-item-label">{{ productsTab.name }}</span>
          </a>
        </template>
      </div>

      <div
        class="products-menu__panel"
        role="tabpanel"
        :aria-label="activeTab.name"
        @keydown.left.prevent="focusActiveNavItem">
        <h4 class="products-menu__panel-title">{{ activeTab.name }}</h4>
        <p class="products-menu__panel-desc">
          {{ activeTab.description }}
          <a
            v-if="activeTab.learnMoreLink"
            :href="toAbsoluteLink(activeTab.learnMoreLink)"
            target="_blank"
            @click.prevent="
              onActionLinkClick(LEARN_MORE_TEXT, toAbsoluteLink(activeTab.learnMoreLink), '_blank')
            ">
            {{ LEARN_MORE_TEXT }}
          </a>
        </p>
        <template v-if="activeTab.id === 'zoom-developers'">
          <div class="products-menu__developers-layout">
            <div
              v-for="category in activeTab.items"
              :key="category.id"
              class="products-menu__developers-column">
              <h4 class="products-menu__panel-title products-menu__panel-title--sub">
                {{ category.label }}
              </h4>
              <p class="products-menu__panel-desc products-menu__panel-desc--sub">
                {{ category.description }}
              </p>
              <ul
                role="list"
                :aria-label="activeTab.name"
                class="products-menu__item-list products-menu__item-list--single-col">
                <li v-for="item in category.items" :key="item.id" class="products-menu__item">
                  <a
                    class="products-menu__item-link"
                    :href="toAbsoluteLink(item.link)"
                    target="_blank"
                    @click.prevent="onJump(item)">
                    <zm-icon size="20" class="products-menu__item-icon">
                      <component :is="productIconsMap[item.id]" />
                    </zm-icon>
                    <span class="products-menu__item-label">{{ item.label }}</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </template>
        <template v-else>
          <ul
            role="list"
            :aria-label="activeTab.name"
            class="products-menu__item-list"
            :style="{ '--products-menu-item-rows': regularItemRows }">
            <li v-for="item in regularItems" :key="item.id" class="products-menu__item">
              <a
                class="products-menu__item-link"
                :href="toAbsoluteLink(item.link)"
                target="_blank"
                @click.prevent="onJump(item)">
                <zm-icon size="20" class="products-menu__item-icon">
                  <component :is="productIconsMap[item.id]" />
                </zm-icon>
                <span class="products-menu__item-label">{{ item.label }}</span>
              </a>
            </li>
          </ul>

          <template v-if="AIProductivitySuiteItems.length">
            <h4 class="products-menu__panel-title products-menu__panel-title--ai-suite mgt-xl">
              <zm-icon size="24" class="products-menu__item-icon">
                <MarkProductAiSuite />
              </zm-icon>
              {{ $t('AIProductivitySuite.title') }}
            </h4>
            <p class="products-menu__panel-desc">
              {{ $t('AIProductivitySuite.desc') }}
              <a
                :href="toAbsoluteLink(AI_SUITE_LINK)"
                target="_blank"
                @click.prevent="
                  onActionLinkClick(LEARN_MORE_TEXT, toAbsoluteLink(AI_SUITE_LINK), '_blank')
                ">
                {{ LEARN_MORE_TEXT }}
              </a>
            </p>
            <ul
              class="products-menu__item-list"
              :style="{ '--products-menu-item-rows': aiSuiteItemRows }">
              <li v-for="item in AIProductivitySuiteItems" :key="item.id">
                <a
                  class="products-menu__item-link"
                  :href="toAbsoluteLink(item.link)"
                  target="_blank"
                  @click.prevent="onJump(item)">
                  <zm-icon size="20" class="products-menu__item-icon">
                    <component :is="productIconsMap[item.id]" />
                  </zm-icon>
                  <span class="products-menu__item-label">{{ item.label }}</span>
                </a>
              </li>
            </ul>
          </template>
        </template>
        <p
          v-if="activeTab.extraItems"
          class="products-menu__panel-desc-extra mgt-lg"
          v-html="activeTab.extraItems"></p>
      </div>

      <div class="products-menu__featured">
        <img
          class="products-menu__featured-image"
          src="https://st1.zoom.us/fe-static/fe-pricing-v3/img/product-my-notes.CieUQ47Q.png"
          :alt="$t('featuredProduct')" />
        <h5 class="products-menu__featured-title">
          🚀
          <zm-badge text="New" color="blue" />
          {{ featuredContent.title }}
        </h5>
        <p class="products-menu__featured-desc">{{ featuredContent.description }}</p>
        <a
          :href="toAbsoluteLink(featuredContent.learnMoreLink)"
          :aria-label="$t('aria.learnMoreAbout', [featuredContent.title])">
          {{ LEARN_MORE_TEXT }}
        </a>
      </div>
    </div>

    <div class="products-menu__footer">
      <div class="products-menu__footer-item">
        <a
          :href="DOWNLOAD_URL"
          class="products-menu__download-link"
          target="_blank"
          @click.prevent="onActionLinkClick($t('downloadCenter'), DOWNLOAD_URL, '_blank')">
          <span class="products-menu__download-icon">
            <zm-icon size="24"><MarkProductDownload /></zm-icon>
          </span>
          <div class="products-menu__download-content">
            <p class="products-menu__download-subtitle">{{ $t('installOnDesktop') }}</p>
            <div class="products-menu__download-title">{{ $t('downloadCenter') }}</div>
          </div>
        </a>
      </div>

      <div class="products-menu__footer-item products-menu__contact">
        <div id="talk-to-sales-rep-label" class="products-menu__contact-label">{{ $t('getInTouch') }}</div>
        <p class="products-menu__contact-number">
          <a
            :href="PHONE_TEL_LINK"
            class="products-menu__contact-link"
            rel="noopener noreferrer"
            aria-describedby="talk-to-sales-rep-label"
            @click.prevent="onActionLinkClick(PHONE_NUMBER, PHONE_TEL_LINK, '_self')">
            {{ PHONE_NUMBER }}
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue'
import { ZmIcon, ZmBadge } from '@zoom/zoom-ui-vue3'
import productsMenu from './data.json'
import { locale } from '../../../../../shared/locales'
import { productIconsMap } from './product-icons'
import { ChevronRightV2 } from '@zoom/icons-vue'
import MarkProductDownload from './icon-components/download.vue'
import { trackEvent } from '../../../../../shared/event-tracker'
import MarkProductAiSuite from './icon-components/ai-suite.vue'
import { createAppI18n } from '../../../../../shared/setup-i18n'

const { t: $t } = createAppI18n('commonHeader').global

// --- Constants ---
const LEARN_MORE_TEXT = $t('learnMore')
const DOWNLOAD_URL = 'https://zoom.us/download'
const PHONE_NUMBER = '+1.888.799.9666 / +1.888.303.1012'
const PHONE_TEL_LINK = `tel:${PHONE_NUMBER}`
const AI_SUITE_LINK = '/en/products/ai-productivity-suite/'
const AI_PRODUCT_IDS = ['paper', 'slides', 'sheets', 'canvas']
const DEFAULT_TAB_ID = 'zoom-workplace'
const AI_TAB_ID = 'zoom-ai'
const UI_LOCATION = 'universal nav - products menu'

// --- Data ---
const productsTabs = productsMenu[locale]
const enUsProductsTabs = productsMenu['en-US'].main_categories ?? []

// --- State ---
const activeTabId = ref(DEFAULT_TAB_ID)
const navItemRefs = ref({})

// --- Computed ---
const mainCategories = computed(() => productsTabs.main_categories ?? [])

const activeTab = computed(
  () => mainCategories.value.find((tab) => tab.id === activeTabId.value) ?? {}
)

const featuredContent = computed(() => productsTabs.featured_content ?? {})

const regularItems = computed(() =>
  (activeTab.value.items ?? []).filter((item) => !AI_PRODUCT_IDS.includes(item.id))
)

const regularItemRows = computed(() => Math.ceil(regularItems.value.length / 2))

const AIProductivitySuiteItems = computed(() => {
  if (activeTab.value.id !== AI_TAB_ID) return []
  return (activeTab.value.items ?? []).filter((item) => AI_PRODUCT_IDS.includes(item.id))
})

const aiSuiteItemRows = computed(() => Math.ceil(AIProductivitySuiteItems.value.length / 2))

// --- Helpers ---
const toAbsoluteLink = (link) => {
  if (!link) return ''
  if (/^https?:\/\//.test(link) || link.startsWith('tel:')) return link
  return `https://www.zoom.com${link}`
}

const getEnUsLabelById = (id) => {
  if (!id) return ''
  for (const tab of enUsProductsTabs) {
    for (const group of tab?.items ?? []) {
      const target = (group?.items ?? []).find((menuItem) => menuItem.id === id)
      if (target?.label) return target.label
    }
  }
  return ''
}

// --- Actions ---
const onJump = (item) => {
  const linkText = getEnUsLabelById(item.id) || item.label
  const linkUrl = toAbsoluteLink(item.link)
  trackEvent({ linkText, linkUrl, uiLocation: UI_LOCATION })
  if (linkUrl) window.open(linkUrl, '_blank')
}

const onTabChange = (tabId) => {
  if (!tabId) return
  activeTabId.value = tabId
}

const onActionLinkClick = (linkText, linkUrl, target = '_blank') => {
  trackEvent({ linkText, linkUrl, uiLocation: UI_LOCATION })
  if (linkUrl) window.open(linkUrl, target)
}

// --- Focus management ---
const setNavItemRef = (el, tabId) => {
  if (!tabId) return
  if (el) {
    navItemRefs.value[tabId] = el
  } else {
    delete navItemRefs.value[tabId]
  }
}

const focusNavByIndex = async (index) => {
  const target = mainCategories.value[index]
  if (!target?.id) return
  await nextTick()
  navItemRefs.value[target.id]?.focus?.()
}

const focusActiveNavItem = async () => {
  const targetId = activeTabId.value || mainCategories.value[0]?.id
  if (!targetId) return
  await nextTick()
  navItemRefs.value[targetId]?.focus?.()
}

const onNavItemKeydown = async (event, tab, index) => {
  const total = mainCategories.value.length
  if (!total) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      await focusNavByIndex((index + 1) % total)
      break
    case 'ArrowUp':
      event.preventDefault()
      await focusNavByIndex((index - 1 + total) % total)
      break
    case 'ArrowRight':
      event.preventDefault()
      if (tab?.items?.length) onTabChange(tab.id)
      break
  }
}
</script>

<style lang="scss" scoped>
.products-menu {
  width: 100%;
  max-width: 1280px;
  border: 1px solid var(--zoom-color-border-subtle-neutral);
  border-radius: 20px;
  background-color: var(--zoom-color-fill-default);
  box-shadow:
    0 12px 24px 0 var(--zoom-color-underlay-dropShadow),
    0 6px 12px 0 var(--zoom-color-underlay-dropShadow);
}

.products-menu__body {
  display: flex;
  gap: 24px;
  padding: 16px 16px 0;
}

.products-menu__nav {
  display: flex;
  flex-direction: column;
  width: 260px;
  gap: 4px;
  padding-right: 16px;
  border-right: 1px solid #e6f0ff;
}

.products-menu__nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 10px 16px;
  border-radius: 12px;
  line-height: 20px;
  cursor: pointer;

  &.is-active {
    background-color: var(--zoom-color-fill-subtler-primary);
    color: var(--zoom-color-text-primary);
  }

  &:not(.is-active):hover {
    background-color: var(--zoom-color-state-subtle-neutral-hover);
    color: var(--zoom-color-text-stronger-neutral);
  }
}

.products-menu__nav-item-label {
  font-size: 14px;
  font-weight: bold;
  color: var(--zoom-color-text-stronger-neutral);
  cursor: pointer;
}

.products-menu__panel {
  flex: 1;
  min-width: 500px;
  padding-bottom: 16px;
}

.products-menu__panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 32px;
  color: var(--zoom-color-text-stronger-neutral);

  &--ai-suite {
    font-size: 16px;
  }
  &--sub {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: var(--zoom-color-text-stronger-neutral);
  }
}

.products-menu__panel-desc {
  margin: 4px 0 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #d1def2;
  font-size: 14px;
  line-height: 24px;
  color: var(--zoom-color-text-neutral);

  &--sub {
    font-size: 12px;

    margin-bottom: 0;
    border-bottom: none;
  }
  a {
    color: var(--zoom-color-text-primary);
  }
}

.products-menu__developers-layout {
  display: flex;
  gap: 20px;
  margin-top: 10px;

  .products-menu__item-list {
    margin-top: 0;
  }
  .products-menu__developers-column {
    width: 50%;
  }
}

.products-menu__item-list {
  display: grid;
  grid-template-rows: repeat(var(--products-menu-item-rows, 1), minmax(0, auto));
  grid-auto-columns: minmax(210px, 1fr);
  grid-auto-flow: column;
  gap: 4px 20px;
  margin-top: 10px;
  width: 100%;

  &--single-col {
    flex: 1;
    grid-auto-flow: row;
    grid-template-columns: 1fr;
  }
}

.products-menu__item-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--zoom-color-text-stronger-neutral);

  &:hover {
    color: #111827;
    background-color: var(--zoom-color-state-subtle-neutral-hover);
    text-decoration: none;
  }
}

.products-menu__item-icon {
  color: var(--zoom-color-text-primary);
}

.products-menu__item-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #111827;
}

.products-menu__featured {
  width: 300px;
  padding-bottom: 40px;
}

.products-menu__featured-image {
  width: 100%;
  min-height: 240px;
  border-radius: 16px;
  object-fit: cover;
  margin-bottom: 16px;
}

.products-menu__featured-title {
  margin: 12px 0 6px;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--zoom-color-text-stronger-neutral);
  display: flex;
  align-items: center;
  gap: 4px;
}

.products-menu__featured-desc {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--zoom-color-text-neutral);
}

.products-menu__featured-cta {
  display: inline-block;
  margin-top: 8px;
  font-size: 14px;
  color: var(--zoom-color-text-primary);
}

.products-menu__footer {
  display: flex;
  align-items: center;
  padding: 16px 24px 12px;
  border-bottom-right-radius: 20px;
  border-bottom-left-radius: 20px;
  background-color: #e6f0ff;
}

.products-menu__download-link {
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover,
  &:focus {
    text-decoration: none;

    .products-menu__download-icon {
      color: var(--zoom-color-text-primary-hover);
    }
  }
}

.products-menu__download-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  text-align: center;
}

.products-menu__download-subtitle {
  margin-bottom: 0;
  font-size: 12px;
  color: var(--zoom-color-text-strong-neutral);
}

.products-menu__download-title {
  font-weight: 500;
  color: var(--zoom-color-text-stronger-neutral);
}

.products-menu__contact {
  margin-left: 48px;
}

.products-menu__contact-label {
  margin-bottom: 0;
  font-size: 12px;
  color: var(--zoom-color-text-strong-neutral);
}

.products-menu__contact-number {
  margin-bottom: 0;
}

.products-menu__contact-link {
  font-size: 16px;
  font-weight: 600;
  color: var(--zoom-color-text-stronger-neutral);
}
</style>
